import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, UserRound, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { FLAGS } from "@/lib/constants";
import { Panel, Empty } from "@/pages/shared";

export default function Verbeteringen() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});

  const saveNote = async (p) => {
    const note = (notes[p.id] ?? p.note ?? "").trim();
    try {
      await api.patch(`/pitches/${p.id}`, { note });
      setRows((rs) => rs.map((r) => (r.id === p.id ? { ...r, note } : r)));
      setNotes((n) => { const { [p.id]: _, ...rest } = n; return rest; });
      toast.success(t("pitch_note_saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const p = await api.get("/pitches");
      setRows(p.data.filter((x) => x.controle_nodig));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // Statuswijziging leidt naar de juiste werkflow: vrij -> bezet = aankomst, bezet -> vrij = vertrek
  const toArrival = async (p) => {
    try {
      await api.patch(`/pitches/${p.id}`, { controle_nodig: false });
      const stay = (await api.get(`/pitches/${p.id}/stay`)).data?.stay;
      navigate(stay?.reservation_id ? `/reservaties?id=${stay.reservation_id}` : `/reservaties?pitch=${encodeURIComponent(p.label)}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resolve = async (p) => {
    try {
      await api.patch(`/pitches/${p.id}`, { controle_nodig: false });
      if (p.status === "bezet") {
        const stay = (await api.get(`/pitches/${p.id}/stay`)).data?.stay;
        navigate(stay?.reservation_id ? `/vertrekkend?id=${stay.reservation_id}` : `/vertrekkend?pitch=${encodeURIComponent(p.label)}`);
        return;
      }
      await load();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel title={`${t("needs_attention")} (${rows.length})`} testid="improvements-panel">
      {rows.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("zone")}</TableHead>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("flags")}</TableHead>
              <TableHead>{t("note")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`improvement-row-${p.label}`}>
                <TableCell><span className="font-semibold text-primary">{p.label}</span></TableCell>
                <TableCell>{p.zone}</TableCell>
                <TableCell className="font-semibold text-foreground">{p.guest_name || "—"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1.5">
                    {p.controle_nodig && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: FLAGS.controle_nodig.color + "22", color: FLAGS.controle_nodig.color }}>
                        <span className="h-2 w-2 rounded-full" style={{ background: FLAGS.controle_nodig.color }} />
                        {FLAGS.controle_nodig[lang] || FLAGS.controle_nodig.nl}
                      </span>
                    )}
                    {p.rood_lintje && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: FLAGS.rood_lintje.color + "22", color: FLAGS.rood_lintje.color }}>
                        <span className="h-2 w-2 rounded-full" style={{ background: FLAGS.rood_lintje.color }} />
                        {FLAGS.rood_lintje[lang] || FLAGS.rood_lintje.nl}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  <div className="flex items-center gap-1.5">
                    <Input value={notes[p.id] ?? (p.note || "")} placeholder={t("pitch_note_placeholder")}
                      onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && saveNote(p)}
                      className="h-8 min-w-[12rem] text-sm" data-testid={`note-input-${p.label}`} />
                    {(notes[p.id] ?? (p.note || "")) !== (p.note || "") && (
                      <Button size="sm" variant="outline" onClick={() => saveNote(p)} data-testid={`note-save-${p.label}`}>
                        <Save className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap space-x-2">
                  <Button size="sm" variant="outline" onClick={() => toArrival(p)} data-testid={`occupy-${p.label}`}>
                    <UserRound className="h-4 w-4 mr-1" />{t("mark_occupied")}
                  </Button>
                  <Button size="sm" onClick={() => resolve(p)} data-testid={`resolve-${p.label}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" />{t("mark_free")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

    </Panel>
  );
}
